import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Car, Wrench, CheckCircle, Tag, ShoppingCart, FileX, Image, Megaphone,
  Receipt, ClipboardText, ArrowRight, Plus, Camera, UserPlus, Warning,
  CaretRight, Clock, CurrencyEur, CalendarBlank, Lightbulb, Eye,
  ChartBar, Wallet, Circle, PencilSimple, TrendUp, WarningCircle, User,
  Bell, MapPin, Timer, Prohibit
} from "@phosphor-icons/react";

const STATUS_LABELS = {
  en_stock: "En stock", en_preparation: "En préparation", pret_a_vendre: "Prêt à vendre",
  reserve: "Réservé", vendu: "Vendu", archive: "Archivé"
};

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

const fmtEur = (v) => v != null ? `${Number(v).toLocaleString('fr-BE')} €` : '—';

/* ─── Welcome Header ─── */
const WelcomeHeader = ({ user }) => (
  <div className="mb-8">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Bonjour {user?.name} <span className="inline-block animate-wave origin-[70%_70%]">👋</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">Voici votre activité aujourd'hui</p>
      </div>
      <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-50 px-3 py-1.5 rounded-lg shrink-0">
        <CalendarBlank size={15} />
        {new Date().toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>
    </div>
  </div>
);

/* ─── Today Tasks (Checklist) — BIGGER with action buttons ─── */
const PRIO_CFG = {
  high: { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50", border: "border-red-100", btnBg: "bg-red-600 hover:bg-red-700", icon: WarningCircle },
  medium: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-100", btnBg: "bg-amber-600 hover:bg-amber-700", icon: Warning },
  low: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-100", btnBg: "bg-emerald-600 hover:bg-emerald-700", icon: CheckCircle },
};

const BTN_LABELS = { high: "Corriger", medium: "Traiter", low: "Optionnel" };

const TodayTasks = ({ tasks }) => {
  if (!tasks?.length) return (
    <div className="dash-card p-6 mb-8 border-l-4 border-l-emerald-500" data-testid="today-tasks">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><CheckCircle size={26} weight="fill" className="text-emerald-600" /></div>
        <div><p className="font-bold text-slate-900">Tout est en ordre !</p><p className="text-sm text-slate-500">Aucune tâche urgente pour aujourd'hui</p></div>
      </div>
    </div>
  );
  return (
    <div className="dash-card p-6 mb-8 border-l-4 border-l-blue-600" data-testid="today-tasks">
      <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-base">
        <ClipboardText size={22} weight="fill" className="text-blue-600" /> Aujourd'hui tu dois
      </h2>
      <div className="space-y-3">
        {tasks.map((t, i) => {
          const s = PRIO_CFG[t.priority] || PRIO_CFG.medium;
          const PIcon = s.icon;
          return (
            <Link key={i} to={t.link}
              className={`flex items-center gap-4 px-4 py-3.5 rounded-xl border transition-all hover:shadow-md active:scale-[0.995] group ${s.bg} ${s.border}`}
              data-testid={`task-${i}`}>
              <PIcon size={22} weight="fill" className={s.text} />
              <span className={`flex-1 font-semibold text-sm ${s.text}`}>{t.text}</span>
              <span className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-transform group-hover:scale-105 ${s.btnBg}`}>
                {BTN_LABELS[t.priority]}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Quick Actions ─── */
const QuickActions = () => {
  const navigate = useNavigate();
  const actions = [
    { icon: Plus, label: "Ajouter véhicule", cls: "bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/25", onClick: () => navigate("/vehicules?action=new") },
    { icon: Camera, label: "Ajouter photos", cls: "bg-slate-800 hover:bg-slate-900 text-white shadow-slate-800/25", onClick: () => navigate("/vehicules") },
    { icon: Megaphone, label: "Publier annonce", cls: "bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/25", onClick: () => navigate("/vehicules") },
    { icon: UserPlus, label: "Ajouter client", cls: "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/25", onClick: () => navigate("/clients?action=new") },
  ];
  return (
    <div className="mb-8" data-testid="quick-actions">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {actions.map((a) => (
          <button key={a.label} onClick={a.onClick} data-testid={`quick-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`flex items-center justify-center sm:justify-start gap-2.5 px-4 py-4 rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl active:scale-[0.96] hover:-translate-y-0.5 ${a.cls}`}>
            <a.icon size={20} weight="bold" /><span className="hidden sm:inline">{a.label}</span><span className="sm:hidden text-xs">{a.label.split(' ').pop()}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── KPI Card (enhanced hover) ─── */
const KpiCard = ({ icon: Icon, value, label, color, bgColor, link }) => {
  const inner = (
    <div className="dash-card p-5 flex items-center gap-4 group cursor-pointer hover:-translate-y-0.5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110" style={{ backgroundColor: bgColor }}>
        <Icon size={24} weight="bold" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-2xl font-extrabold text-slate-900 leading-none tracking-tight">{value}</p>
        <p className="text-xs text-slate-500 mt-1.5 font-medium">{label}</p>
      </div>
      {link && <ArrowRight size={16} className="text-slate-200 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0" />}
    </div>
  );
  return link ? <Link to={link} className="block">{inner}</Link> : inner;
};

/* ─── Mini Chart (with empty state) ─── */
const MiniChart = ({ data }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const hasData = data.some(d => d.count > 0);
  return (
    <div className="dash-card p-5 relative overflow-hidden" data-testid="mini-chart">
      <h2 className="section-label flex items-center gap-2 mb-4"><ChartBar size={14} /> Ventes — 4 dernières semaines</h2>
      {hasData ? (
        <div className="flex items-end gap-4 h-28">
          {data.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <span className="text-sm font-extrabold text-slate-900">{d.count}</span>
              <div className="w-full rounded-xl bg-blue-100 relative overflow-hidden transition-all duration-500" style={{ height: `${Math.max((d.count / max) * 100, 12)}%` }}>
                <div className="absolute inset-0 bg-gradient-to-t from-blue-600 to-blue-500 rounded-xl" />
              </div>
              <span className="text-[11px] font-semibold text-slate-400">{d.label}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-end gap-4 h-28">
          {data.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <span className="text-sm font-bold text-slate-300">0</span>
              <div className="w-full rounded-xl bg-slate-100 transition-all" style={{ height: '12%' }}>
                <div className="absolute inset-0 bg-slate-200 rounded-xl" />
              </div>
              <span className="text-[11px] font-semibold text-slate-300">{d.label}</span>
            </div>
          ))}
        </div>
      )}
      {!hasData && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
          <div className="text-center px-4">
            <TrendUp size={24} className="text-slate-300 mx-auto mb-1" />
            <p className="text-sm font-semibold text-slate-400">Aucune vente ce mois</p>
            <p className="text-xs text-slate-400">Le graphique s'animera avec vos premières ventes</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Suggestions Intelligentes (upgraded) ─── */
const SUGG_ICONS = {
  megaphone: Megaphone, wrench: Wrench, camera: Camera, user: User, tag: Tag
};

const Suggestions = ({ suggestions }) => {
  if (!suggestions?.length) return null;
  return (
    <div className="dash-card p-5 bg-gradient-to-br from-white via-white to-amber-50/30" data-testid="suggestions">
      <h2 className="font-bold text-slate-900 text-sm mb-4 flex items-center gap-2">
        <Lightbulb size={20} weight="fill" className="text-amber-500" /> Suggestions intelligentes
      </h2>
      <div className="space-y-2.5">
        {suggestions.map((s, i) => {
          const SIcon = SUGG_ICONS[s.icon] || Lightbulb;
          const linkTo = s.vehicle_id ? `/vehicules/${s.vehicle_id}` : (s.link || '/vehicules');
          return (
            <Link key={i} to={linkTo}
              className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-100 bg-white hover:border-blue-200 hover:shadow-md hover:-translate-y-px transition-all group"
              data-testid={`suggestion-${i}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-blue-100 transition-colors">
                <SIcon size={18} weight="bold" className="text-blue-600" />
              </div>
              <p className="flex-1 text-sm text-slate-600 leading-relaxed group-hover:text-slate-900 transition-colors">{s.text}</p>
              <CaretRight size={14} className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0 mt-1" />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Action Items (fully clickable with hover) ─── */
const ActionItems = ({ stats }) => {
  const items = [
    { icon: FileX, count: stats?.preparation?.sans_papiers || 0, label: "Dossier incomplet", link: "/vehicules", colorActive: "#dc2626", bgActive: "rgba(220,38,38,0.08)" },
    { icon: Image, count: stats?.preparation?.sans_photos || 0, label: "Sans photos", link: "/vehicules", colorActive: "#d97706", bgActive: "rgba(217,119,6,0.08)" },
    { icon: Megaphone, count: stats?.preparation?.sans_annonce || 0, label: "Sans annonce", link: "/vehicules", colorActive: "#d97706", bgActive: "rgba(217,119,6,0.08)" },
    { icon: ClipboardText, count: stats?.audits?.en_attente || 0, label: "Audits en attente", link: "/vehicules", colorActive: "#2563eb", bgActive: "rgba(37,99,235,0.08)" },
  ];
  return (
    <div className="mb-8" data-testid="action-items">
      <h2 className="section-label mb-3">A traiter</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((item) => {
          const active = item.count > 0;
          return (
            <Link key={item.label} to={item.link}
              className={`dash-card p-4 flex items-center gap-3 group hover:-translate-y-0.5 ${active ? 'border-l-[3px] cursor-pointer' : 'opacity-60'}`}
              style={active ? { borderLeftColor: item.colorActive } : {}}
              data-testid={`action-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                style={{ backgroundColor: active ? item.bgActive : 'rgba(148,163,184,0.06)' }}>
                <item.icon size={22} weight="bold" style={{ color: active ? item.colorActive : '#cbd5e1' }} />
              </div>
              <div>
                <p className="text-2xl font-extrabold leading-none" style={{ color: active ? item.colorActive : '#cbd5e1' }}>{item.count}</p>
                <p className="text-xs text-slate-500 mt-0.5 font-medium">{item.label}</p>
              </div>
              {active && <ArrowRight size={14} className="ml-auto text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Recent Vehicles (photo thumbnails + hover + quick actions) ─── */
const RecentVehiclesTable = ({ vehicles }) => {
  if (!vehicles?.length) return null;
  return (
    <div className="mb-8" data-testid="recent-vehicles">
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-label">Derniers véhicules</h2>
        <Link to="/vehicules" className="text-sm text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1 group">
          Tout voir <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <div className="dash-card overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th className="w-14"></th>
              <th>Véhicule</th>
              <th className="hidden sm:table-cell">Réf.</th>
              <th>Statut</th>
              <th className="hidden md:table-cell">Prix</th>
              <th className="hidden lg:table-cell">Date</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.vehicle_id} className="group hover:bg-blue-50/40 transition-colors">
                <td className="!p-2">
                  <div className="w-11 h-11 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center border border-slate-200">
                    {v.photo_url ? (
                      <img src={v.photo_url} alt="" className="w-full h-full object-cover" loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : null}
                    {!v.photo_url && <Car size={20} className="text-slate-300" />}
                  </div>
                </td>
                <td>
                  <p className="font-bold text-slate-900 text-sm">{v.marque} {v.modele}</p>
                </td>
                <td className="hidden sm:table-cell">
                  <span className="text-xs text-slate-400 font-mono bg-slate-50 px-1.5 py-0.5 rounded">{v.reference_interne}</span>
                </td>
                <td><span className={`badge badge-${v.statut}`}>{STATUS_LABELS[v.statut] || v.statut}</span></td>
                <td className="hidden md:table-cell text-sm font-bold text-slate-700">{fmtEur(v.prix_vente)}</td>
                <td className="hidden lg:table-cell text-slate-400 text-xs">{v.created_at ? new Date(v.created_at).toLocaleDateString('fr-BE') : '—'}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1 opacity-40 group-hover:opacity-100 transition-all">
                    <Link to={`/vehicules/${v.vehicle_id}`}
                      className="p-2 rounded-lg hover:bg-blue-100 text-blue-600 transition-all hover:scale-110" title="Modifier"
                      data-testid={`edit-vehicle-${v.vehicle_id}`}>
                      <PencilSimple size={16} weight="bold" />
                    </Link>
                    <Link to={`/vehicules/${v.vehicle_id}`}
                      className="p-2 rounded-lg hover:bg-amber-100 text-amber-600 transition-all hover:scale-110" title="Publier"
                      data-testid={`publish-vehicle-${v.vehicle_id}`}>
                      <Megaphone size={16} weight="bold" />
                    </Link>
                    <Link to={`/vehicules/${v.vehicle_id}`}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-all hover:scale-110" title="Voir"
                      data-testid={`view-vehicle-${v.vehicle_id}`}>
                      <Eye size={16} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── Activity Feed ─── */
const ACTION_ICONS = {
  create: { icon: Plus, color: "#059669", bg: "rgba(5,150,105,0.08)" },
  update: { icon: Wrench, color: "#2563eb", bg: "rgba(37,99,235,0.08)" },
  delete: { icon: Warning, color: "#dc2626", bg: "rgba(220,38,38,0.08)" },
};

const ActivityFeed = ({ logs }) => {
  if (!logs?.length) return null;
  return (
    <div data-testid="activity-feed" className="mb-8">
      <h2 className="section-label mb-3">Activité récente</h2>
      <div className="dash-card divide-y divide-slate-50/80">
        {logs.map((log, i) => {
          const cfg = ACTION_ICONS[log.action] || ACTION_ICONS.update;
          const IconComp = cfg.icon;
          return (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50/50 transition-colors group">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: cfg.bg }}>
                <IconComp size={14} weight="bold" style={{ color: cfg.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-600 truncate">
                  <span className="font-bold text-slate-900">{log.entity_name || log.entity_type}</span>
                  {' — '}{log.description}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <Clock size={11} /> {getTimeAgo(log.created_at)} {log.user_name && <><Circle size={3} weight="fill" /> {log.user_name}</>}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Alerts Panel ─── */
const ALERT_ICONS = {
  blocked: Prohibit,
  overdue: Timer,
  chez_prestataire: MapPin,
  stale: Clock,
};
const ALERT_SEVERITY_COLORS = {
  high: "border-l-red-500 bg-red-50/50 dark:bg-red-950/20",
  medium: "border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20",
  low: "border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20",
};
const ALERT_ICON_COLORS = {
  high: "text-red-600 dark:text-red-400",
  medium: "text-amber-600 dark:text-amber-400",
  low: "text-blue-600 dark:text-blue-400",
};

const AlertsPanel = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="mb-8" data-testid="alerts-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bell size={18} weight="fill" className="text-red-500" />
        <h2 className="section-label mb-0">Rappels &amp; alertes ({alerts.length})</h2>
      </div>
      <div className="space-y-2">
        {alerts.slice(0, 8).map((alert, idx) => {
          const Icon = ALERT_ICONS[alert.type] || WarningCircle;
          return (
            <Link key={`${alert.type}-${alert.task_id || alert.vehicle_id}-${idx}`}
              to={`/vehicules/${alert.vehicle_id}`}
              className={`flex items-center gap-3 p-3 rounded-lg border-l-4 transition-all hover:shadow-sm ${ALERT_SEVERITY_COLORS[alert.severity] || ALERT_SEVERITY_COLORS.low}`}
              data-testid={`alert-item-${idx}`}>
              <Icon size={20} weight="fill" className={ALERT_ICON_COLORS[alert.severity] || ALERT_ICON_COLORS.low} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {alert.vehicle_name}
                  {alert.type_prestation && <span className="text-slate-500 dark:text-slate-400 font-normal"> - {alert.type_prestation}</span>}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{alert.message}{alert.prestataire_nom ? ` (${alert.prestataire_nom})` : ""}</p>
              </div>
              <CaretRight size={16} className="text-slate-400 flex-shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Main Dashboard ─── */
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [prepStats, setPrepStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, prepRes, alertsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/preparation-stats`).catch(() => ({ data: null })),
        axios.get(`${API}/alerts/active`).catch(() => ({ data: { alerts: [] } })),
      ]);
      setStats(statsRes.data);
      setPrepStats(prepRes.data);
      setAlerts(alertsRes.data?.alerts || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-10 w-10"></div></div>;
  }

  return (
    <div data-testid="dashboard-page" className="animate-fade-in max-w-[1400px]">
      <WelcomeHeader user={user} />
      <TodayTasks tasks={stats?.tasks_today} />
      <QuickActions />
      <AlertsPanel alerts={alerts} />

      {/* KPI: Stock + Performance side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 className="section-label mb-3">Stock véhicules</h2>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard icon={Car} value={stats?.vehicles?.total || 0} label="Total véhicules" color="#0f172a" bgColor="rgba(15,23,42,0.05)" link="/vehicules" />
            <KpiCard icon={CheckCircle} value={stats?.vehicles?.pret_a_vendre || 0} label="Prêts à vendre" color="#059669" bgColor="rgba(5,150,105,0.07)" link="/vehicules" />
            <KpiCard icon={Wrench} value={stats?.vehicles?.en_preparation || 0} label="En préparation" color="#d97706" bgColor="rgba(217,119,6,0.07)" link="/vehicules" />
            <KpiCard icon={Tag} value={stats?.vehicles?.reserves || 0} label="Réservés" color="#7c3aed" bgColor="rgba(124,58,237,0.07)" link="/vehicules" />
          </div>
        </div>
        <div>
          <h2 className="section-label mb-3">Performance</h2>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard icon={ShoppingCart} value={stats?.vehicles?.vendus_mois || 0} label="Ventes ce mois" color="#059669" bgColor="rgba(5,150,105,0.07)" />
            <KpiCard icon={Receipt} value={stats?.financial?.factures_impayees || 0} label="Factures impayées" color="#dc2626" bgColor="rgba(220,38,38,0.07)" link="/factures" />
            <KpiCard icon={Wallet} value={fmtEur(stats?.vehicles?.valeur_stock)} label="Valeur du stock" color="#0f172a" bgColor="rgba(15,23,42,0.05)" />
            <KpiCard icon={CurrencyEur} value={fmtEur(stats?.financial?.ca_mois || 0)} label="CA du mois" color="#059669" bgColor="rgba(5,150,105,0.07)" />
          </div>
        </div>
      </div>

      {/* Financial Dashboard Block */}
      {stats?.financial && (stats.financial.achats_mois > 0 || stats.financial.ca_mois > 0) && (
        <div className="mb-8">
          <h2 className="section-label mb-3">Finances du mois</h2>
          <div className="card p-5">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-orange-600">{fmtEur(stats.financial.achats_mois || 0)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Achats vehicules</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-amber-600">{fmtEur(stats.financial.frais_preparation_mois || 0)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Preparation</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-red-600">{fmtEur(stats.financial.depenses_totales_mois || 0)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total depenses</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-600">{fmtEur(stats.financial.ca_mois || 0)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">CA ventes</p>
              </div>
              <div className="text-center">
                <p className={`text-xl font-bold ${(stats.financial.marge_mois || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtEur(stats.financial.marge_mois || 0)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Marge nette</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preparation Dashboard Block */}
      {prepStats && prepStats.total_tasks > 0 && (
        <div className="mb-8">
          <h2 className="section-label mb-3">Véhicules en préparation</h2>
          <div className="card p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{prepStats.vehicles_en_preparation}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Véhicules en cours</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{prepStats.vehicles_bloques}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Bloqués</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{prepStats.tasks_by_status?.termine || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Tâches terminées</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{prepStats.total_cost?.toLocaleString('fr-BE')} €</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Coût total</p>
              </div>
            </div>

            {/* Tasks by type */}
            {Object.keys(prepStats.tasks_by_type || {}).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(prepStats.tasks_by_type).map(([type, count]) => (
                  <span key={type} className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    {count} {type}
                  </span>
                ))}
              </div>
            )}

            {/* Vehicles chez prestataire */}
            {prepStats.chez_prestataire?.length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Chez prestataire</p>
                <div className="space-y-1.5">
                  {prepStats.chez_prestataire.map(v => (
                    <Link key={v.vehicle_id} to={`/vehicules/${v.vehicle_id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                      <span className="text-sm text-slate-900 dark:text-slate-100 font-medium">{v.vehicle_name}</span>
                      <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">{v.prestataire_nom}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chart + Suggestions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <MiniChart data={stats?.ventes_semaines} />
        <Suggestions suggestions={stats?.suggestions} />
      </div>

      <ActionItems stats={stats} />
      <RecentVehiclesTable vehicles={stats?.recent_vehicles} />
      <ActivityFeed logs={stats?.recent_logs} />
    </div>
  );
};

export default Dashboard;
